/* ------------------------------------------------------------------
 * size-fit-analytics.js
 *
 * Instruments the unified Size & Fit modal (assets/size-fit-modal.js +
 * snippets/pdp-size-fit-modal.liquid) with dual-pipe analytics
 * (window.dataLayer + Shopify.analytics.publish). Matches the
 * pattern from mobile-nav.liquid + search-modal.liquid +
 * home-analytics.js.
 *
 * Events emitted:
 *   - size_chart_open               { product_handle, product_id, source }
 *   - size_chart_close              { product_handle, dwell_ms, close_source }
 *   - size_chart_find_my_size_click { product_handle }
 *       — fires when the user activates the "Find my size" tab
 *         (the TrueFit / sizing-recommendation hook).
 *
 * Source: defaults to "pdp_size_fit_button". When the trigger sits
 * inside a [data-is-quick-view="true"] container the modal was opened
 * from the quick-view sheet → source = "quick_view". When inside a
 * lookbook / shop-the-look companion → "shop_the_look".
 *
 * Product handle extraction:
 *   1. Closest [data-product-handle] on the trigger ancestor (PDP top
 *      doesn't always expose it, but Shop-the-Look + lookbook do).
 *   2. Fallback: derive from window.location.pathname when on
 *      template-product.
 * Product id:
 *   1. Closest [data-product-id]. Falls back to data-current-product-id
 *      on the PDP wrapper which is the variant id (close enough as a
 *      distinguishing key for first-pass reports).
 * ------------------------------------------------------------------ */
(function () {
  if (window.__rastahSizeFitAnalyticsInit) return;
  window.__rastahSizeFitAnalyticsInit = true;

  function track(event, props) {
    var tmplMatch = String(document.body.className || '').match(/template-(\S+)/);
    var payload = Object.assign({
      event: event,
      ts: Date.now(),
      page_path: window.location.pathname + window.location.search,
      page_template: tmplMatch ? tmplMatch[1] : 'unknown'
    }, props || {});
    (window.dataLayer = window.dataLayer || []).push(payload);
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, payload);
      }
    } catch (_) { /* never let tracking break navigation */ }
  }

  /* ---- Context extraction ------------------------------------------ */
  function inferSource(trigger) {
    if (!trigger) return 'pdp_size_fit_button';
    if (trigger.closest('[data-is-quick-view="true"], .quick-product')) return 'quick_view';
    if (trigger.closest('.shop-the-look-product, .lookbook-product, [data-shop-the-look], [data-lookbook-product]')) return 'shop_the_look';
    return 'pdp_size_fit_button';
  }

  function pathHandle() {
    try {
      var p = window.location.pathname.split('/').filter(Boolean);
      var i = p.indexOf('products');
      if (i >= 0 && p[i + 1]) return p[i + 1];
    } catch (_) {}
    return '';
  }

  function ctxFromTrigger(trigger) {
    var handle = '';
    var product_id = '';
    if (trigger) {
      var hCard = trigger.closest('[data-product-handle]');
      if (hCard) handle = hCard.getAttribute('data-product-handle') || '';
      var idCard = trigger.closest('[data-product-id]');
      if (idCard) product_id = idCard.getAttribute('data-product-id') || '';
    }
    if (!handle) handle = pathHandle();
    if (!product_id) {
      // PDP wrapper exposes current variant id under data-current-product-id
      var wrap = document.querySelector('[data-section-type="product"][data-current-product-id]');
      if (wrap) product_id = wrap.getAttribute('data-current-product-id') || '';
    }
    return { handle: handle, product_id: product_id };
  }

  /* ---- State -------------------------------------------------------- */
  var modal = null;
  var openedAt = 0;
  var currentHandle = '';
  var currentProductId = '';
  var currentSource = '';
  var pendingCloseSource = '';

  /* ---- Trigger capture --------------------------------------------- *
   * Capture-phase document listener runs before size-fit-modal.js's
   * own click handler. We read context off the trigger and stash it
   * for the open event.
   */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var openTrigger = t.closest('[data-open-size-fit]');
    if (openTrigger) {
      var ctx = ctxFromTrigger(openTrigger);
      currentHandle = ctx.handle;
      currentProductId = ctx.product_id;
      currentSource = inferSource(openTrigger);
      return;
    }
    if (!modal || modal.hidden) return;

    // "Find my size" tab activation inside the open modal
    var tab = t.closest('[data-size-fit-tab]');
    if (tab && tab.getAttribute('data-size-fit-tab') === 'truefit') {
      track('size_chart_find_my_size_click', {
        product_handle: currentHandle || pathHandle()
      });
      return;
    }
    // Close click
    if (t.closest('[data-size-fit-close]')) {
      pendingCloseSource = t.closest('.size-fit-modal__overlay') ? 'scrim' : 'x_button';
    }
  }, true);

  document.addEventListener('keydown', function (e) {
    if (!modal || modal.hidden) return;
    if (e.key === 'Escape' || e.keyCode === 27) pendingCloseSource = 'esc';
  }, true);

  /* ---- Modal lifecycle --------------------------------------------- *
   * size-fit-modal.js teleports the modal to <body> on first bind, so
   * we have to find it after that. The `hidden` attribute is the
   * authoritative open/closed signal (assets/size-fit-modal.js:1021,1150).
   */
  function bindModal() {
    modal = document.querySelector('[data-size-fit-modal]');
    if (!modal) return false;

    var wasHidden = modal.hidden;
    var obs = new MutationObserver(function () {
      var nowHidden = modal.hidden;
      if (nowHidden === wasHidden) return;
      wasHidden = nowHidden;
      if (!nowHidden) {
        openedAt = Date.now();
        pendingCloseSource = '';
        track('size_chart_open', {
          product_handle: currentHandle || pathHandle(),
          product_id: currentProductId || '',
          source: currentSource || 'pdp_size_fit_button'
        });
      } else {
        if (openedAt) {
          track('size_chart_close', {
            product_handle: currentHandle || pathHandle(),
            dwell_ms: Date.now() - openedAt,
            close_source: pendingCloseSource || 'unknown'
          });
        }
        openedAt = 0;
        pendingCloseSource = '';
      }
    });
    obs.observe(modal, { attributes: true, attributeFilter: ['hidden'] });
    return true;
  }

  /* ---- Boot --------------------------------------------------------- *
   * The modal markup is rendered from snippets/pdp-size-fit-modal.liquid
   * on PDP templates only. On other templates it doesn't exist — the
   * script just noops.
   */
  function boot() {
    if (!bindModal()) {
      // Late hydration (rare) — try again on window.load and once on
      // first open-trigger click (in case the modal is injected by
      // quick-view AJAX before size-fit-modal.js has bound it).
      window.addEventListener('load', bindModal, { once: true });
      document.addEventListener('click', function onceClick(e) {
        if (e.target && e.target.closest && e.target.closest('[data-open-size-fit]')) {
          setTimeout(function () {
            if (bindModal()) document.removeEventListener('click', onceClick, true);
          }, 50);
        }
      }, true);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
